import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { type ModuleData, getModuleDisplayInfo, getModuleColors, getModuleIcon } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { getGrowthStage } from '../../mastery';
import { DjayGrowthState } from '../shared/DjayGrowthState';
import { soundManager } from '../../utils/soundManager';
interface WorldTileProps {
  module: ModuleData;
  selectedGroup: GroupId;
  onClick: () => void;
  getModuleMasteryForGroup: (moduleId: string) => number;
  getAnsweredQuestions: (moduleId: string) => unknown[];
  index?: number;
  hasDecay?: boolean;
}
export function WorldTile({
  module,
  selectedGroup,
  onClick,
  getModuleMasteryForGroup,
  getAnsweredQuestions,
  index = 0,
  hasDecay = false
}: WorldTileProps) {
  const mastery = getModuleMasteryForGroup(module.id);
  const answered = getAnsweredQuestions(module.id).length;
  const isStarted = answered > 0 || mastery > 0;
  const isMastered = mastery >= 95;
  const colors = getModuleColors(module.color);
  const Icon = getModuleIcon(module.icon);
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const growthStage = getGrowthStage(mastery);
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8,
    y: 20
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} transition={{
    delay: index * 0.05,
    type: 'spring',
    bounce: 0.5
  }} whileHover={isStarted || !isStarted ? {
    y: -8,
    scale: 1.05
  } : {}} whileTap={{
    scale: 0.95
  }} className="w-full max-w-[170px] sm:max-w-[180px] cursor-pointer group relative" onClick={() => {
    soundManager.playNavigate();
    onClick();
  }} data-id="element-2824" data-mp="3d9lxnb29nj61">
      
      {/* Decay Badge */}
      {hasDecay && !isMastered && <div className="absolute -top-2 -right-2 z-30" data-id="element-2825" data-mp="etb31i7eo3jo">
          <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="bg-teal-500 text-white rounded-full p-1.5 shadow-md border-2 border-white" title="Herhaling nodig" data-id="element-2826" data-mp="9uq0vd8lf9sl">
          
            <RefreshCw className="w-4 h-4" data-id="element-2827" data-mp="37ob8dv9wkur8" />
          </motion.div>
        </div>}

      {/* Main Tile Container */}
      <div className={`relative flex flex-col items-center p-4 sm:p-5 rounded-[2rem] shadow-sm transition-shadow duration-300 group-hover:shadow-xl border-4 border-white
          ${isMastered ? 'bg-gradient-to-b from-emerald-300 to-emerald-500' : isStarted ? `bg-gradient-to-b ${colors.from} ${colors.to}` : 'bg-slate-100'}`} data-id="element-2828" data-mp="3cl9zs626mnlq">
        
        {/* Top Badge (Emoji or Lock) - Hidden on mobile */}
        <div className="hidden sm:block absolute -top-4 left-1/2 -translate-x-1/2 z-20" data-id="element-2829" data-mp="3vdvrce623s0i">
          <motion.div initial={{
          y: 10,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} className={`drop-shadow-md bg-white rounded-full w-10 h-10 flex items-center justify-center border-4 ${isMastered ? 'border-emerald-100' : isStarted ? colors.border : 'border-slate-200'}`} data-id="element-2830" data-mp="469i860kixc9">
            
            {isMastered ? <span className="text-2xl" data-id="element-2831" data-mp="1tnetjr5x7oaq">👑</span> : isStarted ? <DjayGrowthState masteryScore={mastery} size="sm" data-id="element-2832" data-mp="1tmltf91gvx1k" /> : <Lock className="w-4 h-4 text-slate-400" data-id="element-2833" data-mp="1ooahmsrm3cgp" />}
          </motion.div>
        </div>

        {/* Icon Circle */}
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-inner mb-2 sm:mb-3 mt-1 sm:mt-2 group-hover:scale-110 transition-transform duration-300" data-id="element-2834" data-mp="1jqscldgpthdz">
          <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${isMastered ? 'text-emerald-500' : isStarted ? colors.text : 'text-slate-400'}`} strokeWidth={2.5} data-id="element-2835" data-mp="2cf7kzh5cd1gc" />
          
        </div>

        {/* Title */}
        <h3 className={`font-black text-center text-sm sm:text-base leading-tight mb-2 sm:mb-3 w-full line-clamp-2
          ${isMastered || isStarted ? 'text-white drop-shadow-sm' : 'text-slate-600'}`} data-id="element-2836" data-mp="23dgi0k26r8q6">
          
          {title}
        </h3>

        {/* Progress Area — kindvriendelijke labels, geen percentages.
             Voor 9-12-jarigen zegt "47%" niets emotioneels; de growth-stage label
             (Net begonnen / Groeit / Goed op weg / Sterk / Meester!) en de
             voortgangsbalk geven samen voldoende signaal. */}
        <div className="w-full mt-auto" data-id="element-2837" data-mp="2avwhzwnmwnn2">
          {isMastered ? <div className="bg-white/20 rounded-full py-1.5 px-2 flex items-center justify-center gap-1 backdrop-blur-sm" data-id="element-2838" data-mp="2awfqkmre9zof">
              <CheckCircle2 className="w-4 h-4 text-white" data-id="element-2839" data-mp="2yjxoz155zw0d" />
              <span className="text-xs font-black text-white uppercase tracking-wider" data-id="element-2840" data-mp="33jiiux5h9e68">
                Meester!
              </span>
            </div> : isStarted ? <div className="space-y-1.5" data-id="element-2841" data-mp="2tmdwaw7pzgow">
              <div className="flex justify-center items-center px-1" data-id="element-2842" data-mp="2tlciwl38ei40">
                <span className="text-[10px] font-bold text-white/95 uppercase tracking-wider" data-id="element-2843" data-mp="1e5dpyh26qsoc">
                  {growthStage.label}
                </span>
              </div>
              <div className="h-2.5 w-full bg-black/10 rounded-full overflow-hidden shadow-inner" role="progressbar" aria-valuenow={Math.round(mastery)} aria-valuemin={0} aria-valuemax={100} aria-label={`Voortgang: ${growthStage.label}`} data-id="element-2844" data-mp="1anzh3q3sd3c0">
              
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${mastery}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut',
              delay: 0.2
            }} className="h-full bg-white rounded-full" data-id="element-2845" data-mp="1klne6mov6mxc" />
              
              </div>
            </div> : <div className="bg-white/50 rounded-full py-1.5 px-2 flex items-center justify-center backdrop-blur-sm" data-id="element-2846" data-mp="mwkd7swd2dyn">
              <span className="text-xs font-black text-slate-500 uppercase tracking-wider" data-id="element-2847" data-mp="wwup9a6ntfc8">
                Nieuw
              </span>
            </div>}
        </div>
      </div>
    </motion.div>;
}